import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtDate, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { toast } from "sonner";
import { DownloadSimple, ShieldCheck, Warning, Coins, Percent, LinkBreak, FilePdf } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

const ISSUE_LABEL = { currency_mismatch: "Currency mismatch", missing_vat: "VAT missing", unlinked_po: "No-PO reference" };
const ISSUE_BADGE = { currency_mismatch: "bg-orange-100 text-orange-700", missing_vat: "bg-purple-100 text-purple-700", unlinked_po: "bg-sky-100 text-sky-700" };

export default function InvoiceIntegrity() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | currency | vat | unlinked
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/reports/invoice-integrity").then((r) => setData(r.data)); }, []);

  const reissueAll = async () => {
    if (!window.confirm(`Reissue all ${data.unlinked_po_count} flagged No-PO invoice(s) from their referenced Client PO? Each is restated in place (keeps its number); any that are paid or in a locked period are skipped.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/reports/invoice-integrity/reissue-all");
      const { reissued_count, skipped_count } = res.data;
      toast.success(`Reissued ${reissued_count} invoice(s)${skipped_count ? ` · ${skipped_count} skipped` : ""}`);
      const r = await api.get("/reports/invoice-integrity"); setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk reissue failed"); }
    finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter((r) =>
    filter === "all" ? true
      : filter === "currency" ? r.issues.includes("currency_mismatch")
        : filter === "vat" ? r.issues.includes("missing_vat")
          : r.issues.includes("unlinked_po")
  );

  const exportCols = [
    { key: "invoice_no", label: "Invoice No" }, { key: "lx_po_no", label: "Somcore PO" },
    { key: "client_po_no", label: "Client PO" }, { key: "client_name", label: "Client" },
    { key: "date", label: "Date" }, { key: "issues", label: "Issues", format: (v) => (v || []).map((x) => ISSUE_LABEL[x]).join("; ") },
    { key: "stored_currency", label: "Stored Ccy" }, { key: "expected_currency", label: "Expected Ccy" },
    { key: "exclusive_total", label: "Exclusive", format: fmtNum }, { key: "vat_amount", label: "VAT", format: fmtNum },
    { key: "total_amount", label: "Total", format: fmtNum },
    { key: "correction_route", label: "Suggested Route" },
  ];

  return (
    <Layout>
      <PageHeader
        title="Invoice Integrity Scan"
        subtitle="Read-only audit — issued client invoices affected by the currency or VAT bugs. Nothing is modified; review and correct each one case-by-case."
        actions={<div className="flex items-center gap-2">
          {(data?.unlinked_po_count || 0) > 0 && <Button data-testid="reissue-all-no-po" disabled={busy} onClick={reissueAll}>{busy ? "Reissuing…" : `Reissue all No-PO (${data.unlinked_po_count})`}</Button>}
          <Button variant="outline" data-testid="export-integrity" onClick={() => exportToCsv("invoice-integrity", data?.rows || [], exportCols)}><DownloadSimple size={16} className="mr-1" />CSV</Button>
          <Button variant="outline" data-testid="export-integrity-pdf" onClick={() => openPdf("pdf/invoice-integrity")}><FilePdf size={16} className="mr-1" />PDF</Button>
        </div>}
      />
      <div className="p-8">
        {!data ? (
          <div className="text-sm text-gray-500">Scanning…</div>
        ) : (
          <>
            <KpiStrip cols={4}>
              <KpiCard icon={Warning} label="Affected Invoices" value={data.count} tone={data.count ? "danger" : "info"} testid="kpi-integrity-total" />
              <KpiCard icon={Coins} label="Currency Mismatches" value={data.currency_mismatch_count} tone="accent" testid="kpi-integrity-currency" />
              <KpiCard icon={Percent} label="Missing VAT" value={data.missing_vat_count} tone="accent" testid="kpi-integrity-vat" />
              <KpiCard icon={LinkBreak} label="No-PO References" value={data.unlinked_po_count || 0} tone="accent" testid="kpi-integrity-unlinked" />
            </KpiStrip>

            <div className="mb-3 flex items-center gap-2">
              <Button size="sm" variant={filter === "all" ? "default" : "outline"} data-testid="integrity-filter-all" onClick={() => setFilter("all")}>All ({data.count})</Button>
              <Button size="sm" variant={filter === "currency" ? "default" : "outline"} data-testid="integrity-filter-currency" onClick={() => setFilter("currency")}>Currency ({data.currency_mismatch_count})</Button>
              <Button size="sm" variant={filter === "vat" ? "default" : "outline"} data-testid="integrity-filter-vat" onClick={() => setFilter("vat")}>VAT ({data.missing_vat_count})</Button>
              <Button size="sm" variant={filter === "unlinked" ? "default" : "outline"} data-testid="integrity-filter-unlinked" onClick={() => setFilter("unlinked")}>No-PO ({data.unlinked_po_count || 0})</Button>
            </div>

            {rows.length === 0 ? (
              <EmptyState icon={ShieldCheck} testid="integrity-empty" title="No affected invoices" message="Every issued client invoice has the correct currency and VAT treatment. Nothing to correct." />
            ) : (
              <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                    <th className="px-4 py-2 text-left">Invoice No</th><th className="px-4 py-2 text-left">Somcore PO</th>
                    <th className="px-4 py-2 text-left">Client</th><th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Issue</th><th className="px-4 py-2 text-left">Currency</th>
                    <th className="px-4 py-2 text-right">Exclusive</th><th className="px-4 py-2 text-right">VAT</th>
                    <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2 text-left">Suggested Fix</th>
                    <th className="px-4 py-2 text-right">·</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.invoice_id} data-testid={`integrity-row-${r.invoice_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono-num font-medium">{r.invoice_no}</td>
                        <td className="px-4 py-2 font-mono-num">{r.lx_po_no || "—"}</td>
                        <td className="px-4 py-2">{r.client_name || "—"}</td>
                        <td className="px-4 py-2 lx-num">{fmtDate(r.date)}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {r.issues.map((iss) => (
                              <span key={iss} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ISSUE_BADGE[iss] || "bg-gray-100 text-gray-600"}`}>{ISSUE_LABEL[iss]}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          {r.issues.includes("currency_mismatch")
                            ? <span className="lx-num"><span className="text-red-600 line-through">{r.stored_currency}</span> → <span className="font-semibold text-emerald-700">{r.expected_currency}</span></span>
                            : <span className="lx-num text-gray-500">{r.stored_currency}</span>}
                        </td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(r.exclusive_total)}</td>
                        <td className={`px-4 py-2 text-right lx-num ${r.issues.includes("missing_vat") ? "font-semibold text-red-600" : ""}`}>{fmtNum(r.vat_amount)}</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(r.total_amount)}</td>
                        <td className="px-4 py-2">
                          {r.correction_route === "edit"
                            ? <span className="rounded-sm bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" title="Not paid & period open — restate via Edit (reverses & re-posts GL, keeps the number)">Edit / Restate</span>
                            : <span className="rounded-sm bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" title={r.is_allocated ? "Receipts allocated — reverse with a Credit Note, then reissue" : "Period locked/closed — reverse with a Credit Note, then reissue"}>Credit Note + Reissue</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button className="text-xs font-semibold text-[#1B4383] hover:underline" data-testid={`integrity-open-${r.invoice_id}`} onClick={() => navigate(`/transactions/${r.transaction_id}`)}>Open PO</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-xs text-gray-400">This scan only reads data. "Edit / Restate" is available when an invoice is unpaid and its period is still open; otherwise reverse it with a Credit Note and reissue a corrected invoice.</p>
          </>
        )}
      </div>
    </Layout>
  );
}
